// M10-12513
export const einvCategoryMap: Record<string, string> = {
  "Upload IRN/BarCode along with Invoice": "Category 1",
  "Upload E-Inv Aknowledgement Copy separately": "Category 2",
  "Periodic Bulk Upload of E-Invoice": "Category 3",
};
export const einvTypeMap: Record<string, string> = {
  "1": "Category 1",
  "2": "Category 2",
  "3": "Category 3",
};
// Any fields in rejection_data_email_desc beyond the ones already shown explicitly (rejected_by_name,
// rejected_at, rejection_remark) should still surface — e.g. product_code, product_name, mrp for
// price-related rejections. Same "show unknown extra fields" approach as InvoiceEntryScreen.tsx's
// Rejection Details card. rejected_by / rejected_by_role stay excluded there too, so exclude here.
const REJECTION_FIELDS_ALREADY_SHOWN = new Set(["rejected_by_name", "rejected_at", "rejection_remark", "rejected_by", "rejected_by_role","invoice_number","invoice_date"]);

const formatRejectionFieldKey = (key: string) =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getExtraRejectionFields = (rejectionDataEmailDesc: any): Record<string, string> => {
  const extra: Record<string, string> = {};
  if (!rejectionDataEmailDesc || typeof rejectionDataEmailDesc !== "object") return extra;
  Object.entries(rejectionDataEmailDesc).forEach(([key, value]) => {
    if (REJECTION_FIELDS_ALREADY_SHOWN.has(key)) return;
    if (value === null || value === undefined || value === "") return;
    extra[formatRejectionFieldKey(key)] = String(value);
  });
  return extra;
};

// M10-16051: the date the stockist accepted the invoice. Only shown once they have accepted —
// it is NULL on PENDING / NOT_APPLICABLE rows, and cleared again on Unconfirm.
const getStockistAckDateInfo = (row: any): Record<string, string> => {
  if (row?.po_stockist_ack_status !== "ACCEPTED" || !row?.stockist_acknowledge_date) return {};
  const date = new Date(row.stockist_acknowledge_date);
  return {
    "Stockist Ack Date": `${date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}, ${date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`,
  };
};

// M10-12513
// includeRejectionInfo: set to false when the caller renders its own dedicated
// "Rejection Details" section (e.g. InvoiceHomeDetailCard) so these fields
// aren't also shown unstyled in the plain key/value grid.
export const getInvoiceDetailsCardInfo = (invoiceData: any, includeRejectionInfo: boolean = true) => {  //M10-15065
  let response: any = {};
  // M10-13315
  const latest = invoiceData?.invoice?.invoiceRemarks
    .filter((r: any) => r.invoiceRemarksHdr?.invoice_remarks_hdr === 36 || r.invoiceRemarksHdr?.invoice_remarks_hdr === 37)
    .sort((a: any, b: any) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())[0];
  const CARemoved = getCARemarks(latest?.freetxt_remarks)

  const companyReject = {
    "Company Reject Remarks": CARemoved
  }
  // M10-13315

  const rejectionInfo = invoiceData?.rejection_data_email_desc
    ? {
      "Rejection Initated By": invoiceData?.rejection_data_email_desc?.rejected_by_name || "-",
      "Rejected Initated At": invoiceData?.rejection_data_email_desc?.rejected_at
        ? `${new Date(invoiceData?.rejection_data_email_desc?.rejected_at).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}, ${new Date(invoiceData?.rejection_data_email_desc?.rejected_at).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })}`
        : "-",
      "Rejection Approved By": invoiceData?.rejection_review_action_by || "-",
      "Rejected Approved At": invoiceData?.rejection_review_action_date
        ? `${new Date(invoiceData?.rejection_review_action_date).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}, ${new Date(invoiceData?.rejection_review_action_date).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })}`
        : "-",
      "Rejection Remark": invoiceData?.rejection_data_email_desc?.rejection_remark || "-",
      ...getExtraRejectionFields(invoiceData?.rejection_data_email_desc),
    }
    : {};

  // MEDVOL: M10-11303 changes ========================================
  const esc = invoiceData?.escalations?.[0];

  const remarks =
    esc?.escalated_action === "pending"
      ? {
        "Escalated Remark": esc?.escalated_remarks,
        "Escalated Date": esc?.escalated_date
          ? `${new Date(esc?.escalated_date).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}, ${new Date(esc?.escalated_date).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}`
          : "-"
      }
      : ["accepted", "rejected"].includes(esc?.escalated_action || "")
        ? {
          "Escalated Remark": esc?.escalated_remarks,
          "Escalated Date": esc?.escalated_date
            ? `${new Date(esc?.escalated_date).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}, ${new Date(esc?.escalated_date).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}`
            : "-",
          [esc?.escalated_action === "accepted" ? "Escalated Approved By" : "Escalated Rejected By"]: esc?.action_by,
          [esc?.escalated_action === "accepted" ? "Escalated Approved Date" : "Escalated Rejected Date"]: esc?.action_date
            ? `${new Date(esc?.action_date).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}, ${new Date(esc?.action_date).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}`
            : "-",
          "Company Remark": esc?.action_remarks,
          "Escalated By":esc?.escalated_by //M10-11303
        }
        : {};
  // MEDVOL: M10-11303 changes ========================================

  response = {
    Company: invoiceData?.company_name || "-", // Company name
    "Invoice No.": invoiceData?.invoice?.invoice_number || invoiceData?.stockist_invoice_number || "-", // Invoice number
    "Invoice Date": invoiceData?.invoice?.new_invoice_date
      ? `${new Date(invoiceData.invoice.new_invoice_date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}, ${new Date(invoiceData.invoice.new_invoice_date).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`
      : "-",
    "File Upload Date": invoiceData?.created_date
      ? `${new Date(invoiceData.created_date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}, ${new Date(invoiceData.created_date).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`
      : "-",
    ...getStockistAckDateInfo(invoiceData),
    "Order Number": invoiceData?.orderCodes?.map((code: any) => code?.order_code).join(", ") || "-",
    "Counter Code": invoiceData?.counter_code || "-", // Counter //M10-11303
    "Counter Name": invoiceData?.orderCodes?.[0]?.reference_order?.novu_metadata?.counter_name || "-", // Counter //M10-11303
    FUID: invoiceData?.fuid_no || "-", // FUID#
    "Pharmacy Name": invoiceData?.pharmacy_name || "-", // Pharmacy name
    "Pharmacy Code": invoiceData?.pharmacy_code || "-", // Pharmacy code //M10-11303
    City: invoiceData?.state_district_city || "-", // City
    "Link Type": invoiceData?.link_type || "-", // Link type logic
    Stockist: invoiceData?.stockist_name || "-",
    "Stockist Code (CMP)": invoiceData?.company_stockist_code || "-",
    "Stockist Code (MV)": invoiceData?.stockist_medvol_code || "-",
    "Entered By": invoiceData?.invoice?.entered_by || "-",
    "Approved By": invoiceData?.invoice?.approved_by || "-",
    "Module Type":
      invoiceData?.module_code == 4
        ? "KAM"
        : invoiceData?.upload_type === "INST"
          ? "Medvol - Institutional"
          : invoiceData?.upload_type === "NMV"
            ? "Non-Medvol"
            : "Medvol",
    ...companyReject, // M10-13315
    ...(includeRejectionInfo ? rejectionInfo : {}), //M10-15065
    ...remarks, // MEDVOL: M10-11303 changes ========================================
    "Invoice Verified from Company": invoiceData?.invoice?.company_verification_status === "ACCEPTED" ? "Yes" : "No",
    "Order Type": invoiceData?.orderCodes
      ?.map((item: any) => item?.reference_order?.novu_metadata?.order_type)
      ?.filter(Boolean)
      ?.join(', '),
    // Patient Details applies only to module_code 3 invoices
    ...(Number(invoiceData?.module_code) === 3 && {
      "Patient Details":`${invoiceData?.orderCodes?.[0]?.reference_order?.novu_metadata?.patient_details?.name || "-"}-${invoiceData?.orderCodes?.[0]?.reference_order?.novu_metadata?.patient_details?.age || "-"}/${invoiceData?.orderCodes?.[0]?.reference_order?.novu_metadata?.patient_details?.gender || "-"}`,
    }),
  };

  return response;
};

// M10-16202: the E-Way Bill must be accepted before the invoice header is submitted when any linked
// e-way-required order is not e-way exempt. Same rule as the Entry screen's E-Way Bill popup.
export const isEwayBillPending = (invoiceDetails: any): boolean => {
  const hasNonExemptEwayOrder = !!invoiceDetails?.orderCodes?.some(
    (code: any) => code?.reference_order?.novu_metadata?.e_way_required && code?.reference_order?.eway_exempt !== true
  );
  return hasNonExemptEwayOrder && invoiceDetails?.eWayBillDtl?.einvoice_status !== "accepted";
};
export const EWAY_BILL_PENDING_MESSAGE = "E-Way Bill not verified. Please validate the E-Way Bill before submitting the invoice header.";

export const getInvoiceDetailsInfo = (invoiceDetails: any) => {
  let response: any = {};
  //M10-13053
  const yesNoOrEmpty = (value: any) =>
    value === true ? "Yes" : value === false ? "No" : "-";
  //M10-13053

  response = {
    Company: invoiceDetails?.company_name || "-",
    Stockist: invoiceDetails?.stockist_name || "-",
    "GST Applicability": invoiceDetails?.invoice?.gst_validated === true ? "Yes" : "No",  //M10-11546
    "Invoice no.": invoiceDetails?.invoice?.invoice_number || String(invoiceDetails?.stockist_invoice_number) || "-",
    "Stockist code": invoiceDetails?.stockist_medvol_code || "-",
    "GST No.": invoiceDetails?.counter_gst_number || " ",

    "Invoice date": invoiceDetails?.invoice?.new_invoice_date || " ",
    Pharmacy: invoiceDetails?.pharmacy_name || "-",
    "E-Inv.Applicability": invoiceDetails?.is_einv_applicable === true ? "Yes" : "No",
    "E-Inv Exempt": (() => {
      const applicableOrders = invoiceDetails?.orderCodes?.filter((item: any) => item?.reference_order?.novu_metadata?.e_inv_required == "Y") || [];
      return applicableOrders.length > 0 ? applicableOrders.every((item: any) => item?.reference_order?.einv_exempt === true) ? "Yes" : "No" : "-";
    })(),
    
    // "Reference ID": invoiceDetails?.reference_no || "-",
    "Pharmacy code": invoiceDetails?.pharmacy_code || "-",
    "QR/IRN": invoiceDetails?.invoice?.e_inv_ref_type || " ",
    "Order Code": invoiceDetails?.orderCodes?.map((code: any) => code?.order_code).join(", ") || "-",
    Counter: invoiceDetails?.counter_name || invoiceDetails?.customer_name || "-",
    "Irn Number": invoiceDetails?.invoice?.irn || " ",
    "Link Type": invoiceDetails?.link_type || "-",

    "Counter code": invoiceDetails?.counter_code || "-",
    ...((invoiceDetails?.link_type === "DRAPH" || invoiceDetails?.link_type === "INDPH-PH" || invoiceDetails?.link_type === "INDPH-HS")
      &&
      { "DL no.": invoiceDetails?.counter_dl_number || " " }),

    ...(invoiceDetails?.link_type === "DRDS" && {
      "MCI Validated": invoiceDetails?.invoice?.mci_validated === true ? "Yes" : "No",
    }),
    Address: invoiceDetails?.counter_address,
    ...((invoiceDetails?.link_type === "DRAPH" || invoiceDetails?.link_type === "INDPH-PH" || invoiceDetails?.link_type === "INDPH-HS")
      &&
      { "DL Present": invoiceDetails?.invoice?.dl_validated === true ? "Yes" : "No" }),
    ...(invoiceDetails?.link_type === "DRDS" && { "MCI no.": invoiceDetails?.counter_mci_number || " " }),
    "Clinic/Hospital": invoiceDetails?.counter_clinic_name || "-",
    ...(invoiceDetails?.link_type === "INDPH-HS" && {
      "Hospital Name": invoiceDetails?.hospital_name || "-",
    }),

    "District Name": invoiceDetails?.state_district_city?.split(",")[1] || "-",
    City: invoiceDetails?.state_district_city?.split(",")[2] || "-",
    // "MTD Value": "-",
    FUID: invoiceDetails?.fuid_no || "-",
    "Entered By": invoiceDetails?.invoice?.entered_by || "-",
    "Approved By": invoiceDetails?.invoice?.approved_by || "-",
    //M10-15233
    "File Uploaded Date": invoiceDetails?.created_date
      ? `${new Date(invoiceDetails.created_date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}, ${new Date(invoiceDetails.created_date).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`
      : "-",
    ...getStockistAckDateInfo(invoiceDetails),
      //M10-15233
    "Entered Date": invoiceDetails?.invoice?.entered_date //M10-9780
      ? `${new Date(invoiceDetails.invoice.entered_date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}, ${new Date(invoiceDetails.invoice.entered_date).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`
      : "-",
    "Approved Date": invoiceDetails?.invoice?.approved_date //M10-9780
      ? `${new Date(invoiceDetails.invoice.approved_date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}, ${new Date(invoiceDetails.invoice.approved_date).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`
      : "-",
    "Location Name": invoiceDetails?.location_name ? invoiceDetails?.location_name : '-',
    "Location Code": invoiceDetails?.location_code ? invoiceDetails?.location_code : '-',
    ...(invoiceDetails?.is_einv_applicable && {
      "E-Inv Category": einvCategoryMap[invoiceDetails?.einvack_type_code] || "-", invoice: "" ,
    }),
    ...(invoiceDetails?.stamp_and_seal_required && {
      "Stamp & Sign Status": yesNoOrEmpty(invoiceDetails?.invoice?.stamp_and_seal_validated), //M10-13053
      "E-Inv": yesNoOrEmpty(invoiceDetails?.invoice?.is_category_1),  //M10-13053
    }),
    "Auto Entered": invoiceDetails?.invoice?.is_auto_entered == true ? "Yes" : "No",
    "Textracted": invoiceDetails?.invoice?.textract == true ? "Yes" : "No", //M10-11601
    "Po Proportionate Exempt": invoiceDetails?.invoice?.po_proportionate_exempt_invoice == true ? "Yes" : "No", //M10-11703
    // KAM PO-digitize invoices only (module_code 4)
    ...(invoiceDetails?.module_code === 4 && {
      "PO Ref No": invoiceDetails?.invoice?.poid_ref_number || " ", //M10-15732
    }),
    "Module Type":
      invoiceDetails?.module_code == 4
        ? "KAM"
        : invoiceDetails?.upload_type === "INST"
          ? "Medvol - Institutional"
          : invoiceDetails?.upload_type === "NMV"
            ? "Non-Medvol"
            : "Medvol",
    "Invoice Verified from Company": invoiceDetails?.invoice?.company_verification_status === "ACCEPTED" ? "Yes" : "No",
    // Only applicable/mandatory for KAM (module_code 4) invoices — kept as the last row on screen
    ...(invoiceDetails?.module_code === 4 && {
      "Goods Delivered Date*": invoiceDetails?.invoice?.goods_delivered_date || invoiceDetails?.invoice?.new_invoice_date || " ",
    }),
    // Patient Details applies only to module_code 3 invoices
    ...(Number(invoiceDetails?.module_code) === 3 && {
      "Patient Details":`${invoiceDetails?.orderCodes?.[0]?.reference_order?.novu_metadata?.patient_details?.name || "-"}-${invoiceDetails?.orderCodes?.[0]?.reference_order?.novu_metadata?.patient_details?.age || "-"}/${invoiceDetails?.orderCodes?.[0]?.reference_order?.novu_metadata?.patient_details?.gender || "-"}`,
    }),
  };

  return response;
};


export const eInvoiceCardDetailsInfo = (eInvoiceData: any, cardData: any) => {
  let response: any = {};
  // MEDVOL: M10-10639 changes
  response = {
    "Invoice No.": eInvoiceData?.invoice_number || "-",
    "FUID": eInvoiceData?.mapped_fuid_number || "-",
    "IRN No.": eInvoiceData?.irn_number || "-",
    "Stockist Name": cardData?.stockist_name || "-",
    Date: eInvoiceData?.invoice_date || "-",
    "Counter Code": cardData?.counter_code || "-",
    "Order No": cardData?.orderCodes?.map((code: any) => code?.order_code).join(", ") || "-",
    "Link Type": cardData?.link_type || "-",
    "Pharmacy Name": cardData?.pharmacy_name || "-",
    City: cardData?.state_district_city?.split(",")[2] || "-",
    "Stockist Invoice": cardData?.file_path || "-",
    Amount: eInvoiceData?.amount || "-",
    "GST No": eInvoiceData?.seller_gst || "-",
    "Ack. Copy": eInvoiceData?.einvoice_attachment?.einvoice_attachment_path || "-",
    "Stockist Code (CMP)": cardData?.company_stockist_code || "-",
    "Stockist Code (MV)": cardData?.stockist_medvol_code || "-",
    // M10-12130
    "E-Invoice Type": eInvoiceData?.einvoice_attachment?.einvoice_cat ,
    "Uploaded Type":
      eInvoiceData?.einvoice_attachment?.einvoice_cat === "Upload IRN/BarCode along with Invoice"
        ? "Upload IRN/BarCode along with Invoice"
        : /\.(xls|xlsx)$/i.test(eInvoiceData?.einvoice_attachment?.einvoice_attachment_path || "")
          ? "Periodic Bulk Upload of E-Invoice"
          : "Upload E-Inv Aknowledgement Copy separately",
    "E-Invoice Retrieval Response": eInvoiceData?.remarks ? eInvoiceData?.remarks : '-', // M10-13251
    // M10-12130
    // MEDVOL: M10-10639 changes
  };

  return response;
};

export const DEFAULT_TABLE_DATA = [
  {
    type: "Quantity",
    invoiceData: "",
    orderedData: "",
    modifiedData: "",
    remaining_quantity: "",
    allowed_qty: "",
  },
  {
    type: "Discount %",
    invoiceData: "",
    orderedData: "",
    modifiedData: "",
    // discountValue: "",
  },
  {
    type: "Cash Discount",
    invoiceData: "",
    orderedData: "",
    modifiedData: "",
  },
  {
    type: "Scheme Qty",
    invoiceData: "",
    orderedData: "",
    modifiedData: "",
    allowed_free_qty: "",
  },
  {
    type: "PTR",
    invoiceData: "",
    orderedData: "",
    modifiedData: "",
  },
  {
    type: "MRP",
    invoiceData: "",
    orderedData: "",
    modifiedData: "",
  },
  {
    type: "Price",
    invoiceData: "",
    orderedData: "",
    modifiedData: "",
  },
];

export const searchOptions = [
  { label: "FUID No", value: "fuid_no" },
  { label: "Invoice Number", value: "invoice_number" },
  { label: "Order No", value: "order_no" },
  { label: "Counter Code", value: "counter_code" },
  { label: "Stockist Medvol Code", value: "stockist_medvol_code" },
  { label: "Company Stockist Code", value: "company_stockist_code" },
];

export const searchOptionsEscalate = [
  { label: "FUID No", value: "fuid" },
  { label: "Invoice Number", value: "invoice_number" },
  { label: "Counter Code", value: "counter_code" }
];


// M10-12690 - Utility function - allows only standard keyboard characters
export const isValidKeyboardInput = (value: string): boolean => {
  // Matches only standard keyboard characters (ASCII 32-126)
  const keyboardRegex = /^[\x20-\x7E]*$/;
  return keyboardRegex.test(value);
};
// M10-12690
//KAM - INST
export const MODULE_NAME_LIST = [
  { key: 1, value: "Medvol" },
  { key: 2, value: "POB" },
  { key: 3, value: "PUC" },
  { key: 4, value: "KAM" },
];


export const UPLOAD_TYPE_LIST = [
  { key: "NMV", value: "Non-Medvol" },
  { key: "MV", value: "Medvol" },
  { key: "INST", value: "Institutional" },
];
//KAM - INST

//M10-14101
export const ORDER_MODE_LIST = [
  { key: "10", value: "MV MO with Approval" },
  { key: "11", value: "MV MO without Approval" },
  { key: "7", value: "Counter Initiated" },
  { key: "8", value: "Field Initiated" },
  { key: "9", value: "App MO" },
  { key: "2", value: "REGULAR" },
  { key: "4", value: "COUNTER APPROVAL" },
];

export const ORDER_TYPE_LIST = [
  { key: "1", value: "Regular Order" },
  { key: "2", value: "Supply Against Order - No Replenishment" },
  { key: "3", value: "Credit Note - No Replenishment" },
  { key: "4", value: "POB" },
  { key: "5", value: "PUC" },
  // SAO-CN: KAM settlement flow — advance supply at PTS, rate difference settled by credit note.
  { key: "6", value: "Supply Against Order - Credit Note" },
];
//M10-14101

export function generate5CharCode() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < 5; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
// M10-13315
// "Company Rejected" now comes from invoice.company_verification_status, not from sniffing a
// "CA-Remarks:" prefix out of the remark text. The old inference could not tell a company
// rejection apart from an ops recall, and went silent whenever the remark wording changed.
// The remarks are still read here for the "Invoice Reversed" fallback label.
export const latestRemarks = (remarks: any, fuid_no?: any, companyVerificationStatus?: string) => {
  const latest = remarks?.filter((r: any) => r.invoiceRemarksHdr?.invoice_remarks_hdr === 36 || r.invoiceRemarksHdr?.invoice_remarks_hdr === 37)
    .sort((a: any, b: any) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())[0];

  const latestDesc = latest?.invoiceRemarksHdr?.invoice_remarks_desc;
  const finalDesc = latestDesc === "Invoice Removed" || latestDesc === "Invoice Reverted" ? "Invoice Reversed" : "";
  if (fuid_no === 8) return '';
  return companyVerificationStatus === "REJECTED" ? "Company Rejected" : finalDesc
}

export function getCARemarks(text: any) {
  const keyword = "CA-Remarks:";

  if (text?.includes(keyword)) {
    return text?.split(keyword)[1].trim();
  }

  return "";
}
// M10-13315
export const getFinancialYear = (dateInput: string | Date): number => {
  if (dateInput instanceof Date) {
    const m = dateInput.getMonth();
    const y = dateInput.getFullYear();
    return m >= 3 ? y : y - 1;
  }

  const str = dateInput.trim().toLowerCase();

  // Check month name (Apr, April, apr etc.)
  const monthNames = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
  const namedMonth = monthNames.findIndex(m => str.includes(m));

  const nums = str.match(/\d+/g)?.map(Number) ?? [];
  const year = nums.find(n => n > 1000) ?? new Date().getFullYear();
  const rest = nums.filter(n => n <= 1000);

  let month: number;

  if (namedMonth !== -1) {
    month = namedMonth;
  } else {
    const day = rest.find(n => n > 12); // definite day
    month = day
      ? (rest.find(n => n <= 12) ?? 1) - 1  // other part is month
      : (rest[1] ?? 1) - 1;                 // ambiguous → assume dd/mm/yyyy
  }

  return month >= 3 ? year : year - 1;
};

// DateTimeInput treats picked date/time as literal wall-clock values encoded as UTC (see DateTimeInput.tsx),
// so "now" for min/max/comparison purposes must be encoded the same way, or a same-day future time
// wouldn't be caught (or a valid same-day time could be wrongly blocked) due to the local UTC offset.
export const getLiteralNowIso = (): string => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0, 0)).toISOString();
};

// M10-14735: Free Qty / Discount % / Discount Amount are not allowed when Price is less than PTR
// export const isDiscountEntryAllowedForPtr = (price: any, ptr: any): boolean => {
//   return Number(price) >= Number(ptr);
// };

// M10-15166: Price cannot exceed PTR by more than 5%
export const isPriceExceedingPtr = (price: any, ptr: any, tolerancePercent: number = 5): boolean => {
  return Number(price) > Number(ptr) * (1 + tolerancePercent / 100);
};